import { CSSInterpolation } from '@emotion/css/create-instance'
import React, { type Ref } from 'react'

import type { ReactInputProps } from '../../types'
import { warnInDev } from '../../util/logging'
import { DISABLE_PASSWORD_MANAGERS_PROPS } from '../../util/passwordManagers'
import { type InputState, InternalInput } from './internal/InternalInput'
import { InternalInputWrapper } from './internal/InternalInputWrapper'
import { convertToBig, useNumberInput } from './internal/useNumberInput'

type Props = {
  /**
   * The value to show in the input. If empty, any provided `placeholder` is shown.
   */
  value: string
  /**
   * Callback called when the value in the input changes. The value provided will be a valid
   * number string, though it may contain more precision than can be represented by a JS
   * `Number` (e.g. by running the value through `Number.parseFloat`). We recommend using
   * something like [big.js](http://mikemcl.github.io/big.js/) to safely parse these values
   * if you need to operate on them and precision matters.
   *
   * In general you can just provide a callback here and things will work the way you expect.
   * However, it's worth noting that this callback will not be called for a change event if
   * the user has entered an invalid value (e.g. `'10.'` or a value larger than `max`). If
   * the user enters an invalid number, `onChange` will instead be called one final time on
   * input blur with a fully-valid number `value`. As a concrete example of this, imagine the
   * user starts with an empty `<CurrencyInput max="5" value="">` and types `3`, `3`, `<tab>`.
   * This is what would happen:
   *
   * 1. On first `3` input, `onChange` is called with the value `'3'`.
   * 2. On second `3` input, `onChange` is not called because the value is invalid (33 > 5).
   * 3. On `<tab>`, the input is blurred, so the input value of `33` is normalized given the
   *    constraint of `max="5"` and `onChange` is called with a value of `'5'`
   */
  onChange: ({ value }: { value: string }) => void
  /**
   * Specifies the maximum that the value will be constrained to.
   */
  max?: string
  /**
   * Specifies the number of decimal places that the value should be limited to. For example,
   * when buying cryptocurrencies we may want to allow up to 6 decimal places (e.g. `0.835702`).
   *
   * Pass `0` if you only want integers to be accepted. If you pass `0` here, all relevant props
   * (e.g. `max`) should also be passed integer values.
   *
   * If omitted, this will default to either 2 or 4 depending on the current `value`: we allow
   * 4 decimal places in the range (-1, 1) but 2 outside that range. For example, 0.1234 and
   * 3.12 are valid by default but 3.1234 is not. This extra granularity can be useful for
   * things like maintaining specificity for prices of penny stocks.
   *
   * Migration note: <br />
   * In `web-app`'s various inputs (`PlainDecimalInput`, `MoneyInput`, etc.) this prop was
   * called `tickSize`. If you want the equivalent of `web-app`'s `IntegerInput`, pass `0` here.
   */
  maxDecimalPlaces?: number
  /**
   * Whether to disable the input or mark it as having an error.
   */
  state?: InputState
  /**
   * Provide `true` to visually hide the input label, while still leaving it present in the DOM
   * for screen readers. Ignored if no `label` is provided.
   */
  hideLabel?: boolean

  /**
   * A label to show above the input. If you would like to visually hide it please use
   * `hideLabel` prop to do so. If you do not provide a value here, you must provide your own
   * `<label>` element for the input for accessibility reasons.
   */
  label?: string
  /**
   * An error message to show below the input. Will only be shown if `state` is `"error"`.
   */
  errorMessage?: string
  /**
   * Placeholder text to show when the input is empty.
   */
  placeholder?: ReactInputProps['placeholder']
  /**
   * A React ref to attach to the actual `input` element, e.g. for triggering focus or blur on
   * the input.
   */
  innerRef?: Ref<HTMLInputElement>
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do
   * this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
}

type DefaultInputProps = Omit<
  ReactInputProps,
  keyof Props | 'type' | 'disabled' | 'onChange' | 'css' | 'className' | 'style'
>

/**
 * An input element for collecting currency/money values. It only accepts numeric characters
 * and related punctuation. Users can also use the up and down arrow keys to modify the value.
 * It only accepts positive values, optionally up to a maximum. By default it accepts dollars
 * (USD), but it can be configured to accept any single currency or one of multiple. If
 * configured to accept multiple currencies, the input will contain a dropdown menu the user
 * can use to select which currency they'd like to specify the value in.
 *
 * Like all inputs, this should be used inside a `<form>` element. You also may want to use
 * the `getInputState` helper.
 *
 * Note that we have not yet implemented non-USD or multi-currency support, so this input is
 * currently USD-only. If you'd like to use one of these features, please reach out to us in
 * [#bento-design-system-discussion](https://hood.slack.com/archives/CRADQRAE7) to chat with
 * us about it.
 */
export function CurrencyInput({
  errorMessage,
  hideLabel = false,
  innerRef,
  label = '',
  max,
  maxDecimalPlaces,
  onBlur,
  onChange,
  onKeyDown,
  overrideRootStyles,
  placeholder = '$0.00',
  state = 'default',
  value,
  ...inputProps
}: Props & DefaultInputProps) {
  const maxAsBig = convertToBig(max)
  if (max !== undefined && maxAsBig && maxAsBig.lte(0)) {
    warnInDev(`CurrencyInput: max (${max}) should be greater than 0`)
  }

  const numberInputProps = useNumberInput({
    addTrailingZeros: true,
    componentName: 'CurrencyInput',
    enableNumberSeparators: true,
    max,
    maxDecimalPlaces,
    min: '0',
    onBlur,
    onChange,
    onKeyDown,
    removeLeadingZeros: true,
    step: '1',
    textAlign: 'end',
    value,
    visualFormatting: {
      add: addVisualFormatting,
      remove: removeVisualFormatting,
    },
  })

  return (
    <InternalInputWrapper
      errorMessage={errorMessage}
      hideLabel={hideLabel}
      label={label}
      overrideRootStyles={overrideRootStyles}
      state={state}
    >
      {errorMessageId => (
        <InternalInput
          aria-describedby={errorMessageId}
          innerRef={innerRef}
          placeholder={placeholder}
          state={state}
          {...numberInputProps}
          {...DISABLE_PASSWORD_MANAGERS_PROPS}
          {...inputProps}
        />
      )}
    </InternalInputWrapper>
  )
}

/**
 * Helper to add a leading '$' to the specified value, for display.
 */
function addVisualFormatting(value: string): string {
  if (!value) {
    return value
  }

  // Insert the '$' after the '-' for negative values.
  // NOTE(eric.lovett): Note for i18n that this may not work for all locales.
  return value.startsWith('-') ? `-$${value.slice(1)}` : `$${value}`
}

/**
 * Helper to remove any leading '$' from the specified value.
 */
// NOTE(eric.lovett): Note for i18n that this may not work for all locales.
function removeVisualFormatting(value: string): string {
  // If the new value is '-$', then the user must have deleted the final digit from a negative
  // value. Since negative values are invalid, we empty out the entire input.
  if (value === '-$') {
    return ''
  }

  // Remove the '$' from a negative value.
  if (value.startsWith('-$')) {
    return `-${value.slice(2)}`
  }

  // Remove the '$' from a positive value.
  if (value.startsWith('$')) {
    return value.slice(1)
  }

  return value
}
